// client/src/utils/domReconciliationGuard.ts
// Guards against "removeChild"/"insertBefore" NotFoundError (DOMException code 8)
// crashes caused by third-party DOM mutation — most commonly Google Translate /
// the browser's built-in "translate this page" feature and similar extensions.
//
// Those tools rewrite the text nodes that React expects to own. When React later
// unmounts a subtree, its reconciler calls removeChild/insertBefore on a node that
// is no longer a real child of its recorded parent, and the browser throws
// NotFoundError — which white-screens the page through the error boundary
// (see Sentry NODE-CS, observed inside a Radix Select portal on /users).
//
// The well-known mitigation (React issue #11538) is to make these two DOM methods
// fail soft: if the target node isn't actually a child of `this`, no-op instead of
// throwing. This lets a translated/extension-mutated page degrade gracefully rather
// than crash. It only changes behavior in the exact case the browser would have
// thrown anyway, so normal React rendering is unaffected.

import { captureMessage } from '@/lib/sentry';

let installed = false;
const reportedMethods = new Set<string>();

function reportOnce(method: string) {
  if (reportedMethods.has(method)) return;
  reportedMethods.add(method);
  try {
    captureMessage(
      `DOM reconciliation guard caught ${method} on a detached node (likely page translation/extension)`,
      'warning',
      {
        method,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        error_type: 'dom_reconciliation_guard',
      }
    );
  } catch {
    // Sentry not ready / disabled — swallow, the guard still does its job.
  }
}

export function installDomReconciliationGuard() {
  if (typeof window === 'undefined' || typeof Node === 'undefined') return;
  if (installed) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // The node was moved/replaced by a third party — React thinks we still own it.
      reportOnce('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  } as typeof Node.prototype.removeChild;

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node no longer lives here — append instead of throwing so the
      // new node still makes it into the tree.
      reportOnce('insertBefore');
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  } as typeof Node.prototype.insertBefore;
}
